import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import type { User } from "@/types";
import { api } from "@/lib/api";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  match?: "exact" | "prefix";
  aliases?: string[];
}

const NAV: (NavItem & { badgeKey?: "pending" })[] = [
  { to: "/admin/dashboard",     label: "Dashboard",    icon: "▦",  match: "exact"  },
  { to: "/admin/tests",         label: "Test",         icon: "✎",  match: "prefix" },
  { to: "/admin/contatti",      label: "Contatti",     icon: "◉",  match: "prefix", aliases: ["/admin/studenti"], badgeKey: "pending" },
  { to: "/admin/tag",           label: "Tag",          icon: "▣",  match: "prefix" },
  { to: "/admin/campagne",      label: "Campagne",     icon: "◈",  match: "prefix" },
  { to: "/admin/whatsapp",      label: "WhatsApp",     icon: "✆",  match: "prefix" },
  { to: "/admin/impostazioni",  label: "Impostazioni", icon: "⚙",  match: "prefix" },
];

function isActive(pathname: string, item: NavItem): boolean {
  const paths = [item.to, ...(item.aliases ?? [])];
  return paths.some((p) =>
    item.match === "prefix" ? pathname.startsWith(p) : pathname === p,
  );
}

export function AdminLayout({ user }: { user: User }) {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Chiude il drawer al cambio rotta (anche se cliccato da un Link figlio).
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Polling leggero del conteggio pending (ogni 30s). Si refresha anche su
  // ogni cambio rotta cosicché dopo approve/reject il badge si aggiorni.
  useEffect(() => {
    let alive = true;
    const fetchCount = () => api.get<{ count: number }>("/admin/students/pending-count")
      .then((r) => { if (alive) setPendingCount(r.count); })
      .catch(() => { /* silenzioso */ });
    fetchCount();
    const id = setInterval(fetchCount, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [pathname]);

  // Body scroll lock quando il drawer è aperto su mobile.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const displayName = (user.nome || user.cognome)
    ? `${user.nome ?? ""} ${user.cognome ?? ""}`.trim()
    : user.email;

  return (
    <div className="min-h-screen">
      {/* Top bar mobile — visibile solo sotto md */}
      <header className="md:hidden sticky top-0 z-30 bg-card border-b border-border flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Apri menù"
          className="text-primary text-2xl leading-none w-8 h-8 flex items-center justify-center"
        >
          ☰
        </button>
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary">
          QUIZ AFFIANCO
        </div>
      </header>

      <div className="flex">
        {/* Backdrop mobile */}
        {mobileOpen && (
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Chiudi menù"
            className="md:hidden fixed inset-0 bg-black/60 z-40"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            bg-card border-r border-border flex flex-col
            md:w-60 md:shrink-0 md:sticky md:top-0 md:h-screen
            fixed inset-y-0 left-0 w-64 z-50
            transition-transform duration-200 ease-out
            ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
            md:translate-x-0
          `}
        >
          <div className="px-5 py-6 border-b border-border flex items-center justify-between">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary">
                QUIZ AFFIANCO
              </div>
              <div className="text-primary text-sm font-serif mt-1">AI 5.0</div>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Chiudi menù"
              className="md:hidden text-secondary hover:text-primary text-xl leading-none w-7 h-7 flex items-center justify-center"
            >
              ✕
            </button>
          </div>

          <nav className="flex-1 py-3 space-y-0.5 overflow-y-auto">
            {NAV.map((item) => {
              const active = isActive(pathname, item);
              const badge = item.badgeKey === "pending" && pendingCount > 0
                ? pendingCount : null;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`
                    flex items-center gap-3 px-5 py-2.5 text-sm font-mono
                    transition-colors
                    ${active
                      ? "bg-bg text-primary border-l-2 border-accent"
                      : "text-secondary hover:text-primary hover:bg-bg/50 border-l-2 border-transparent"}
                  `}
                >
                  <span className="text-base">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {badge !== null && (
                    <span className="bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                      {badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="px-5 py-4 border-t border-border space-y-2">
            <div className="text-xs">
              <div className="text-primary truncate">{displayName}</div>
              <div className="text-secondary font-mono text-[17px]">{user.role}</div>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem("jwt");
                window.location.href = "/admin";
              }}
              className="w-full text-left font-mono text-[17px] text-secondary hover:text-primary"
            >
              esci →
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
